import { motion } from 'framer-motion';
import { Download } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import IdgateLogoSVG from '../components/IdgateLogoSVG';
import SEOHead from '../components/SEOHead';

const PRESETS = {
  original: {
    blueDark: '#1a3a52', blueLight: '#5B8DBE',
    greenDark: '#2D9B4A', greenLight: '#7FD856',
    id1: '#1a3a52',
    gate1: '#D4A574', gate2: '#E8B84B',
    slogan1: '#D4A574', slogan2: '#D4A574', slogan3: '#D4A574',
    bg: '#FFFFFF', transparent: true,
  },
  light: {
    blueDark: '#1a3a52', blueLight: '#5B8DBE',
    greenDark: '#2D9B4A', greenLight: '#7FD856',
    id1: '#1a3a52',
    gate1: '#D4A574', gate2: '#E8B84B',
    slogan1: '#D4A574', slogan2: '#D4A574', slogan3: '#D4A574',
    bg: '#FFFFFF', transparent: false,
  },
  dark: {
    blueDark: '#7FA8D8', blueLight: '#F1F5F9',
    greenDark: '#2D9B4A', greenLight: '#7FD856',
    id1: '#F1F5F9',
    gate1: '#EFC27D', gate2: '#F5D98A',
    slogan1: '#EFC27D', slogan2: '#EFC27D', slogan3: '#EFC27D',
    bg: '#1a3a52', transparent: false,
  },
  mono: {
    blueDark: '#1a3a52', blueLight: '#4A6B85',
    greenDark: '#1a3a52', greenLight: '#4A6B85',
    id1: '#1a3a52',
    gate1: '#1a3a52', gate2: '#4A6B85',
    slogan1: '#4A6B85', slogan2: '#4A6B85', slogan3: '#4A6B85',
    bg: '#FFFFFF', transparent: false,
  },
};

const PRESET_LABELS = {
  original: 'Original',
  light: 'Light card',
  dark: 'Dark card',
  mono: 'Monochrome navy',
};

const ICON_FIELDS = [
  { id: 'blueDark', label: 'Pole & arm (dark)' },
  { id: 'blueLight', label: 'Pole & arm (light)' },
  { id: 'greenDark', label: 'Checkmark (dark)' },
  { id: 'greenLight', label: 'Checkmark (light)' },
];

const TEXT_FIELDS = [
  { id: 'id1', label: '"ID" text' },
  { id: 'gate1', label: '"Gate" start' },
  { id: 'gate2', label: '"Gate" end' },
  { id: 'slogan1', label: '"Scan." word' },
  { id: 'slogan2', label: '"Verify." word' },
  { id: 'slogan3', label: '"Check-in" word' },
];

const BASE_WIDTH = 1596;
const BASE_HEIGHT = 732;

const PNG_EXPORTS = [
  { id: 'web', label: `Web (1×) — ${BASE_WIDTH}×${BASE_HEIGHT}`, scale: 1 },
  { id: 'creative', label: `Creative / Social (1.5×) — ${BASE_WIDTH * 1.5}×${BASE_HEIGHT * 1.5}`, scale: 1.5 },
  { id: 'banner', label: `Flex Banner (3×) — ${BASE_WIDTH * 3}×${BASE_HEIGHT * 3}`, scale: 3 },
];

const isValidHex = (value) => /^#[0-9a-fA-F]{6}$/.test(value);

const ColorField = ({ id, label, value, onChange }) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <div className="flex items-center gap-2 mb-2.5">
      <label htmlFor={`c-${id}`} className="flex-1 text-sm text-slate-600 whitespace-nowrap">
        {label}
      </label>
      <div
        className="relative w-7 h-7 rounded-md border border-slate-300 overflow-hidden flex-shrink-0 cursor-pointer"
        style={{ background: value }}
      >
        <input
          id={`c-${id}`}
          type="color"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setDraft(e.target.value);
          }}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>
      <input
        className="w-20 text-xs px-1.5 py-1 rounded-md border border-slate-300 bg-white text-slate-700 font-mono focus:outline-none focus:ring-1 focus:ring-primary-500"
        value={draft}
        onChange={(e) => {
          const v = e.target.value;
          setDraft(v);
          if (isValidHex(v)) onChange(v);
        }}
        onBlur={() => setDraft(value)}
      />
    </div>
  );
};

const LogoEditor = () => {
  const [colors, setColors] = useState(PRESETS.original);
  const [pngExportId, setPngExportId] = useState('web');
  const svgRef = useRef(null);

  const updateColor = (fieldId, value) => {
    setColors((prev) => ({ ...prev, [fieldId]: value }));
  };

  const applyPreset = (name) => {
    setColors(PRESETS[name]);
  };

  // Fetches the Philosopher bold + italic WOFF2 files from Google Fonts and
  // returns a <style> string with base64-inlined @font-face rules, so
  // exported SVG/PNG files render the wordmark correctly without relying on
  // the host document's <link> tag (which exported files can't see).
  const buildEmbeddedFontStyle = async () => {
    const cssResponse = await fetch(
      'https://fonts.googleapis.com/css2?family=Philosopher:ital,wght@0,700;1,400&display=swap'
    );
    if (!cssResponse.ok) throw new Error(`Google Fonts CSS fetch failed: ${cssResponse.status}`);
    const cssText = await cssResponse.text();

    // The Google Fonts CSS2 API returns one @font-face block per
    // weight/style/unicode-range combination (latin, latin-ext, cyrillic,
    // etc). Our wordmark text is plain ASCII, so keep only the "latin"
    // subset for each weight/style to avoid embedding unused glyph sets.
    const commentedBlocks = cssText.match(/\/\*\s*([\w-]+)\s*\*\/\s*@font-face\s*{[^}]*}/g) || [];
    const faces = [];
    for (const block of commentedBlocks) {
      const subsetMatch = block.match(/\/\*\s*([\w-]+)\s*\*\//);
      if (!subsetMatch || subsetMatch[1] !== 'latin') continue;
      const urlMatch = block.match(/url\((https:\/\/fonts\.gstatic\.com\/[^)]+)\)/);
      const weightMatch = block.match(/font-weight:\s*([^;]+);/);
      const styleMatch = block.match(/font-style:\s*([^;]+);/);
      if (!urlMatch) continue;
      faces.push({
        url: urlMatch[1],
        weight: weightMatch ? weightMatch[1].trim() : '400',
        style: styleMatch ? styleMatch[1].trim() : 'normal',
      });
    }
    // Fallback: if the "latin"-subset comments aren't present (API format
    // changed), fall back to grabbing every block so the export still works.
    if (faces.length === 0) {
      const allBlocks = cssText.match(/@font-face\s*{[^}]*}/g) || [];
      for (const block of allBlocks) {
        const urlMatch = block.match(/url\((https:\/\/fonts\.gstatic\.com\/[^)]+)\)/);
        const weightMatch = block.match(/font-weight:\s*([^;]+);/);
        const styleMatch = block.match(/font-style:\s*([^;]+);/);
        if (!urlMatch) continue;
        faces.push({
          url: urlMatch[1],
          weight: weightMatch ? weightMatch[1].trim() : '400',
          style: styleMatch ? styleMatch[1].trim() : 'normal',
        });
      }
    }

    const faceRules = await Promise.all(
      faces.map(async (face) => {
        const fontResponse = await fetch(face.url);
        if (!fontResponse.ok) throw new Error(`Font file fetch failed: ${fontResponse.status}`);
        const buffer = await fontResponse.arrayBuffer();
        let binary = '';
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const base64 = btoa(binary);
        return `@font-face{font-family:'Philosopher';font-style:${face.style};font-weight:${face.weight};src:url(data:font/woff2;base64,${base64}) format('woff2');}`;
      })
    );

    return faceRules.join('\n');
  };

  const serializeSVG = async ({ width, height } = {}) => {
    const svgEl = svgRef.current;
    if (!svgEl) return '';
    const clone = svgEl.cloneNode(true);
    clone.setAttribute('width', width ?? BASE_WIDTH);
    clone.setAttribute('height', height ?? BASE_HEIGHT);

    try {
      const fontCSS = await buildEmbeddedFontStyle();
      if (fontCSS) {
        const svgNS = 'http://www.w3.org/2000/svg';
        const styleEl = document.createElementNS(svgNS, 'style');
        styleEl.textContent = fontCSS;
        const defs = clone.querySelector('defs');
        if (defs) {
          defs.insertBefore(styleEl, defs.firstChild);
        } else {
          clone.insertBefore(styleEl, clone.firstChild);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Could not embed Philosopher font in exported SVG; falling back to system font.', err);
    }

    return new XMLSerializer().serializeToString(clone);
  };

  const downloadSVG = async () => {
    const svgString = await serializeSVG();
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'idgate-logo.svg';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadPNG = async () => {
    const preset = PNG_EXPORTS.find((p) => p.id === pngExportId) || PNG_EXPORTS[0];
    const width = BASE_WIDTH * preset.scale;
    const height = BASE_HEIGHT * preset.scale;
    const svgString = await serializeSVG({ width, height });
    const img = new Image();
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      alert('Failed to prepare image for export.');
    };
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((pngBlob) => {
        URL.revokeObjectURL(url);
        if (!pngBlob) {
          alert("This size couldn't be exported in this browser — try a smaller size.");
          return;
        }
        const a = document.createElement('a');
        a.href = URL.createObjectURL(pngBlob);
        a.download = `idgate-logo-${preset.id}-${width}x${height}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
      }, 'image/png');
    };
    img.src = url;
  };

  return (
    <>
      <SEOHead
        title="Logo Color Editor"
        description="Customize the IDGate logo's colors and download it as SVG or high-resolution PNG for web, creatives, or flex banners."
        url="https://idgate.in/logo"
        noindex
      />
      <section className="bg-surface-page py-16 min-h-[calc(100vh-4rem)]">
        <div className="container-custom max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10 text-center"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Logo Color Editor</h1>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Fine-tune the IDGate icon and wordmark colors, apply a preset, and export as SVG or high-resolution PNG.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={`rounded-2xl border border-slate-200 p-6 sm:p-8 mb-6 flex items-center justify-center min-h-[200px] ${
              colors.transparent
                ? 'bg-[repeating-conic-gradient(#e2e8f0_0%_25%,#f8fafc_0%_50%)] bg-[length:16px_16px]'
                : 'bg-white'
            }`}
          >
            <IdgateLogoSVG ref={svgRef} colors={colors} uid="editor" style={{ maxWidth: 560 }} />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="rounded-xl border border-slate-200 bg-white p-5 shadow-soft"
            >
              <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Icon</h3>
              {ICON_FIELDS.map(({ id: fieldId, label }) => (
                <ColorField key={fieldId} id={fieldId} label={label} value={colors[fieldId]} onChange={(v) => updateColor(fieldId, v)} />
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="rounded-xl border border-slate-200 bg-white p-5 shadow-soft"
            >
              <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Wordmark &amp; background</h3>
              {TEXT_FIELDS.map(({ id: fieldId, label }) => (
                <ColorField key={fieldId} id={fieldId} label={label} value={colors[fieldId]} onChange={(v) => updateColor(fieldId, v)} />
              ))}
              <ColorField id="bg" label="Background" value={colors.bg} onChange={(v) => updateColor('bg', v)} />
              <div className="flex items-center gap-2 mb-2.5">
                <label htmlFor="chk-trans" className="flex-1 text-sm text-slate-600">
                  Transparent bg
                </label>
                <input
                  id="chk-trans"
                  type="checkbox"
                  checked={colors.transparent}
                  onChange={(e) => updateColor('transparent', e.target.checked)}
                  className="w-[18px] h-[18px] cursor-pointer accent-primary-600"
                />
              </div>

              <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-4 mb-3">Presets</h3>
              <div className="flex gap-2 flex-wrap">
                {Object.keys(PRESETS).map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => applyPreset(name)}
                    className="text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    {PRESET_LABELS[name]}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="flex gap-3 flex-wrap items-center"
          >
            <button type="button" onClick={downloadSVG} className="btn-secondary inline-flex items-center gap-2">
              <Download size={16} /> Download SVG
            </button>

            <div className="flex items-center gap-2">
              <select
                value={pngExportId}
                onChange={(e) => setPngExportId(e.target.value)}
                className="text-xs px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {PNG_EXPORTS.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
              <button type="button" onClick={downloadPNG} className="btn-secondary inline-flex items-center gap-2">
                <Download size={16} /> Download PNG
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default LogoEditor;
